import type React from "react";

import {
  Fragment,
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
} from "react";
import { Send, Sparkles, Copy, Info, File } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Textarea } from "@/components/ui/textarea";
import { Card, CardContent, CardFooter } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Avatar, AvatarFallback, AvatarImage } from "@/components/ui/avatar";
import { useLoaderData, useLocation } from "react-router-dom";
import type { LoaderData } from "./loader";
import { useSocket } from "@/hooks/useWebsocket";
import { useAuth } from "@/features/authentication/context/auth/auth";
import {
  HoverCard,
  HoverCardContent,
  HoverCardTrigger,
} from "@/components/ui/hover-card";
import { copyToClipboard } from "@/utils/copy-to-clipboard";
import { sendTrackDataServer } from "@/features/analytics/utils/serverTrackData";
interface DataAnalysisResponse {
  id: string;
  excel_url: string;
  demands: number;
  spaces_returned: number;
  private_rooms_returned_ids: number[];
  spaces_returned_ids: number[];
  demands_without_results: number;
  demandas_atendidas: {
    CEP: string;
    "Total de Salas": number;
  }[];
}

export default function MatchMaking() {
  const csvExampleUrl = `${import.meta.env.VITE_NEW_BACKOFFICE_URL}/example.csv`;
  const notFoundMessage =
    "não encontramos nenhuma sala que atenda aos critérios especificados";
  const errorMessage =
    "Houve um erro ao tentar buscar as salas para a localização informada";
  const { url, clientId } = useLoaderData() as LoaderData;
  const messagesEndRef = useRef<HTMLDivElement>(null);
  const { userInfo } = useAuth();
  const [messages, setMessages] = useState<
    { role: string; content: string; type: "text" | "file" }[]
  >([
    {
      role: "assistant",
      content: `Olá, para iniciar a busca, informe pelo menos um desses requisitos via texto ou faça o upload de um arquivo CSV.
- Nome da demanda
- Localização (endereço, bairro, cidade e/ou Estado);
- Capacidade mínima;
- Valor mensal máximo em reais;
- Amenidades, separadas por vírgulas.
Você pode combinar os requisitos de acordo com a necessidade de cada demanda por sala, por exemplo: "buscar salas em Vila Mariana e Centro do Rio para 10 pessoas com TV e salas em Recife no valor máximo de R$ 7.000, com locker e impressora."
☝️ A localização é um requisito obrigatório.`,
      type: "text",
    },
    {
      role: "assistant",
      content: `Download do arquivo CSV de exemplo ${csvExampleUrl}`,
      type: "text",
    },
  ]);
  const [input, setInput] = useState("");
  const [file, setFile] = useState<File | undefined>();

  useEffect(() => {
    if (messages)
      messagesEndRef.current?.scrollIntoView({ behavior: "smooth" });
  }, [messages]);

  const name = useMemo(() => {
    return userInfo?.user?.first_name?.substring(0, 2);
  }, [userInfo]);

  const matchUrl = useCallback((message: string) => {
    return (
      message?.match(/https?:\/\/[^\s]+\.xlsx/) || message?.match(csvExampleUrl)
    );
  }, []);

  const isNotFoundMessage = useCallback((message: string) => {
    return message.includes(notFoundMessage) || message.includes(errorMessage);
  }, []);

  const location = useLocation();

  const fileInputRef = useRef<HTMLInputElement>(null);

  async function sendFile() {
    if (!file) return;
    const timeSinceUpload = Date.now();
    sendTrackDataServer({
      eventName: "SEARCH_CSV_UPLOAD",
      url: location.pathname,
      productType: "PO",
      data: {
        user_id: userInfo?.user?.id?.toString() ?? "",
        email: userInfo?.profile?.email ?? "",
      },
    });
    setMessages((prev) => [
      ...prev,
      { role: "user", content: file.name, type: "file" },
      { role: "assistant", content: "Processando arquivo...", type: "text" },
    ]);
    const formData = new FormData();
    formData.append("file", file);
    formData.append("client_id", clientId);
    if (fileInputRef.current) {
      fileInputRef.current.value = "";
    }
    setFile(undefined);
    await fetch(
      `${import.meta.env.VITE_WOBA_BACKEND_API}/private-offices/csv`,
      {
        method: "POST",
        body: formData,
      },
    )
      .then((res) => res.json())
      .then((data) => {
        const response = data as DataAnalysisResponse;
        sendTrackDataServer({
          eventName: "SEARCH_CSV_RESULT_RETURNED",
          url: location.pathname,
          productType: "PO",
          data: {
            user_id: userInfo?.user?.id?.toString() ?? "",
            email: userInfo?.profile?.email ?? "",
            private_rooms_returned: response.private_rooms_returned_ids.length,
            spaces_returned: response.spaces_returned,
            spaces_returned_ids: response.spaces_returned_ids,
            private_rooms_returned_ids: response.private_rooms_returned_ids,
            demands: response.demands,
            search_duration_ms: Date.now() - timeSinceUpload,
            demands_without_results: response.demands_without_results,
          },
        });
        setMessages((prev) => [
          ...prev,
          {
            role: "assistant",
            content: `Total de salas: ${response.private_rooms_returned_ids.length} encontradas`,
            type: "text",
          },
          { role: "assistant", content: response.excel_url, type: "text" },
        ]);
      })
      .catch((err) => {
        sendTrackDataServer({
          eventName: "SEARCH_CSV_UPLOAD_FAIL",
          url: location.pathname,
          productType: "PO",
          data: {
            user_id: userInfo?.user?.id?.toString() ?? "",
            email: userInfo?.profile?.email ?? "",
            message: err.message,
          },
        });
        setMessages((prev) => [
          ...prev,
          {
            role: "assistant",
            content: "Erro ao processar arquivo",
            type: "text",
          },
        ]);
      });
  }
  const handleSend = async () => {
    if (file) {
      await sendFile();
      return;
    }

    if (input.trim()) {
      setMessages([
        ...messages,
        { role: "user", content: input, type: "text" },
      ]);
      setInput("");
      await fetch(`${import.meta.env.VITE_CHAT_API}/trigger`, {
        method: "POST",
        body: JSON.stringify({
          message: {
            role: "user",
            content: `${input} OBS: client_id: ${clientId}`,
          },
        }),
      });
      sendTrackDataServer({
        eventName: "SEARCH_PROMPT_SENT",
        url: location.pathname,
        productType: "PO",
        data: {
          user_id: userInfo?.user?.id?.toString() ?? "",
          email: userInfo?.profile?.email ?? "",
          text: input,
        },
      });
    }
  };

  const handleKeyDown = async (e: React.KeyboardEvent) => {
    if (e.key === "Enter" && !e.shiftKey) {
      e.preventDefault();
      await handleSend();
    }
  };

  const memoizedTrackData = useCallback(
    (data: { answer: string }) => {
      const urlMatch = matchUrl(data.answer);
      if (isNotFoundMessage(data.answer)) {
        sendTrackDataServer({
          eventName: "SEARCH_PROMPT_RESULT_FAIL",
          url: location.pathname,
          productType: "PO",
          data: {
            user_id: userInfo?.user?.id?.toString() ?? "",
            email: userInfo?.profile?.email ?? "",
            message: data.answer,
          },
        });
      }
      if (urlMatch) {
        const userMessages = messages.filter(
          (message) => message.role === "user",
        );
        const lastUserMessage =
          userMessages[userMessages.length - 1] ?? undefined;
        sendTrackDataServer({
          eventName: "SEARCH_PROMPT_RESULT_RETURNED",
          url: location.pathname,
          productType: "PO",
          data: {
            user_id: userInfo?.user?.id?.toString() ?? "",
            email: userInfo?.profile?.email ?? "",
            text: lastUserMessage?.content ?? "",
          },
        });
      }
    },
    [
      location.pathname,
      userInfo?.profile?.email,
      matchUrl,
      userInfo?.user?.id,
      messages,
      isNotFoundMessage,
    ],
  );

  const handleMessage = useCallback(
    (data: { answer: string }) => {
      memoizedTrackData(data);
      setMessages((prev) => [
        ...prev,
        { role: "assistant", content: data.answer, type: "text" },
      ]);
    },
    [memoizedTrackData],
  );

  useSocket({
    url,
    callback: handleMessage,
  });

  const handleFileChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (file) {
      setFile(file);
    }
  };
  return (
    <div className="flex flex-col h-screen bg-zinc-950">
      <header className="border-b border-zinc-800 p-4 flex items-center justify-between">
        <div className="flex flex-col gap-2 w-full">
          <div className="flex items-center gap-2">
            <Sparkles className="h-5 w-5 text-white" />
            <span className="font-semibold text-white">Woba agent</span>
            <Badge
              variant="outline"
              className="ml-2 text-xs bg-zinc-800 text-zinc-400 hover:bg-zinc-800"
            >
              Preview
            </Badge>
            <HoverCard>
              <HoverCardTrigger className="text-xs text-zinc-400 flex items-center gap-2">
                <Info className="h-4 w-4" />
                <span className="text-xs text-zinc-400">{clientId}</span>
              </HoverCardTrigger>
              <HoverCardContent className="w-80">
                <div className="flex justify-between space-x-4">
                  <div>
                    Em caso de necessidade de suporte, tire um print deste Id ou
                    copie
                  </div>
                  <Button
                    variant="outline"
                    size="icon"
                    className="ml-2"
                    onClick={() => copyToClipboard(clientId)}
                  >
                    <Copy className="h-4 w-4" />
                  </Button>
                </div>
              </HoverCardContent>
            </HoverCard>
          </div>
          <span className="text-xs text-red-500">
            OBS: este projeto é uma POC, caso atualize ou feche a página, a
            conversa será perdida
          </span>
        </div>
      </header>

      <div className="flex-1 overflow-y-auto p-4 space-y-6 container">
        {messages.map((message, index) => {
          const urlMatch = matchUrl(message.content);
          const url = urlMatch ? urlMatch[0] : null;
          const text = message?.content?.replace(url ?? "", "").trim();
          return (
            <div
              key={index}
              className={`flex ${message.role === "user" ? "justify-end" : "justify-start"}`}
            >
              <div
                className={`flex gap-3 ${message.role === "user" ? "flex-row-reverse" : ""}`}
              >
                <Avatar
                  className={`h-8 w-8 ${message.role === "assistant" ? "bg-blue-600" : "bg-zinc-700"}`}
                >
                  {message.role === "assistant" ? (
                    <AvatarImage src="/images/woba-logo.png" />
                  ) : (
                    <>
                      <AvatarImage src={userInfo?.user?.picture} />
                      <AvatarFallback>{name}</AvatarFallback>
                    </>
                  )}
                </Avatar>
                {message.type === "text" && (
                  <div
                    className={`rounded-lg p-3 ${
                      message.role === "assistant"
                        ? "bg-zinc-800 text-white"
                        : "bg-blue-600 text-white"
                    }`}
                  >
                    <div className="text-sm w-full">
                      {text.split("\n").map((line, index) => (
                        <Fragment key={index}>
                          {line}
                          <br />
                        </Fragment>
                      ))}
                    </div>
                    {url && (
                      <a
                        href={url}
                        target="_blank"
                        rel="noopener noreferrer"
                        className="underline text-blue-400 w-full break-all"
                      >
                        {url}
                      </a>
                    )}
                  </div>
                )}
                {message.type === "file" && (
                  <div className="rounded-lg p-3 bg-zinc-800 text-white">
                    <File className="h-4 w-4" />
                    <span>{message.content}</span>
                  </div>
                )}
              </div>
            </div>
          );
        })}
        <div ref={messagesEndRef} />
      </div>

      <Card className="border-t border-zinc-800 rounded-none bg-zinc-900">
        <CardContent className="p-4">
          <div className="relative">
            <Textarea
              placeholder="Message woba assistant..."
              className="min-h-24 resize-none border-zinc-700 bg-zinc-800 text-white focus-visible:ring-blue-600"
              value={input}
              onChange={(e) => setInput(e.target.value)}
              onKeyDown={handleKeyDown}
            />
            <div className="absolute bottom-3 left-3 flex items-center gap-2">
              <Button
                onClick={() => fileInputRef.current?.click()}
                size="icon"
                className="h-8 w-8 rounded-full bg-blue-600 hover:bg-blue-700"
              >
                <File className="h-4 w-4" />
              </Button>
              <p className="text-xs text-white">{file ? file.name : ""}</p>
              <input
                ref={fileInputRef}
                type="file"
                accept=".csv"
                id="file"
                className="hidden"
                onChange={handleFileChange}
              />
            </div>
            <div className="absolute bottom-3 right-3 flex gap-2">
              <Button
                onClick={() => handleSend()}
                size="icon"
                className="h-8 w-8 rounded-full bg-blue-600 hover:bg-blue-700"
              >
                <Send className="h-4 w-4" />
              </Button>
            </div>
          </div>
        </CardContent>
        <CardFooter className="px-4 py-2 border-t border-zinc-800 text-xs text-zinc-500 flex flex-col gap-2 text-start items-start">
          <span>Woba agent vai te ajudar a encontrar salas privativas</span>
        </CardFooter>
      </Card>
    </div>
  );
}
